/**
 * Connection Service
 * Handles connection-related operations with Firebase
 */

import { 
  collection, 
  doc, 
  addDoc,
  setDoc,
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp, 
  orderBy,
  limit 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';
import { getAuth } from 'firebase/auth';
import { createNotification } from '../../../services/firebase/notificationService';

/**
 * Send a connection request to another user
 * Updated with better error messages
 * 
 * @param {string} recipientId - ID of the recipient
 * @param {string} message - Optional message with the request
 * @returns {Promise<string>} Request ID
 */
const sendConnectionRequest = async (recipientId, message = '') => {
  try {
    // Get current user ID
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to send a connection request');
    }
    
    const senderId = currentUser.uid;
    
    // Check if request already exists
    const existingStatus = await getConnectionStatus(senderId, recipientId);
    
    if (existingStatus.status !== 'none') {
      // Provide more specific error messages
      switch (existingStatus.status) {
        case 'connected':
          throw new Error('You are already connected with this user');
        case 'pending':
          if (existingStatus.direction === 'sent') {
            throw new Error('Connection request already sent and pending');
          } else {
            throw new Error('This user has already sent you a connection request. Check your pending requests.');
          }
        case 'accepted':
          throw new Error('You are already connected with this user');
        case 'declined':
          throw new Error('Your previous connection request was declined. Please wait before sending another request.');
        default:
          throw new Error(`Connection request ${existingStatus.status}`);
      }
    }
    
    // Create new request
    const requestData = {
      senderId,
      recipientId,
      message,
      status: 'pending',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const requestRef = await addDoc(collection(db, 'connectionRequests'), requestData);
    
    // Get sender profile for the notification
    const senderProfileRef = doc(db, 'profiles', senderId);
    const senderProfileSnap = await getDoc(senderProfileRef);
    let senderName = 'Someone';
    let senderPhoto = null;
    
    if (senderProfileSnap.exists()) {
      const senderProfile = senderProfileSnap.data();
      senderName = senderProfile.basicInfo?.name || 'Someone';
      senderPhoto = senderProfile.photos && senderProfile.photos.length > 0 ? senderProfile.photos[0] : null;
    }
    
    // Create notification for the recipient
    await createNotification({
      userId: recipientId,
      type: 'connection_request',
      content: {
        requestId: requestRef.id,
        senderId,
        senderName,
        senderPhoto,
        message
      }
    });
    
    return requestRef.id;
  } catch (error) {
    console.error("Error sending connection request:", error);
    throw error;
  }
};

/**
 * Get the connection status between two users
 * Updated to properly check connections collection first
 * 
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {Promise<Object>} Status object { status, requestId, direction }
 */
const getConnectionStatus = async (userId1, userId2) => {
  try {
    // FIRST: Check if users are actually connected in the connections collection
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', userId1),
      limit(10)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const connections = connectionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Check if there's an active connection between the users
    const activeConnection = connections.find(conn => 
      conn.users.includes(userId2) && conn.users.includes(userId1)
    );
    
    if (activeConnection) {
      return { 
        status: 'connected',
        connectionId: activeConnection.id
      };
    }
    
    // SECOND: If not connected, check connection requests
    const sentRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('senderId', '==', userId1),
      where('recipientId', '==', userId2),
      limit(1)
    );
    
    const receivedRequestsQuery = query(
      collection(db, 'connectionRequests'),
      where('senderId', '==', userId2),
      where('recipientId', '==', userId1),
      limit(1)
    );
    
    const [sentSnapshot, receivedSnapshot] = await Promise.all([
      getDocs(sentRequestsQuery),
      getDocs(receivedRequestsQuery)
    ]);
    
    // Check sent requests
    if (!sentSnapshot.empty) {
      const requestDoc = sentSnapshot.docs[0];
      const requestData = requestDoc.data();
      
      // If request was accepted but no connection exists, treat as no connection
      if (requestData.status === 'accepted') {
        return { status: 'none' };
      }
      
      return { 
        status: requestData.status,
        requestId: requestDoc.id,
        direction: 'sent'
      };
    }
    
    // Check received requests
    if (!receivedSnapshot.empty) {
      const requestDoc = receivedSnapshot.docs[0];
      const requestData = requestDoc.data();
      
      // If request was accepted but no connection exists, treat as no connection
      if (requestData.status === 'accepted') {
        return { status: 'none' };
      }
      
      return { 
        status: requestData.status,
        requestId: requestDoc.id,
        direction: 'received'
      };
    }
    
    return { status: 'none' };
    
  } catch (error) {
    console.error("Error checking connection status:", error);
    return { status: 'error', error: error.message };
  }
};
/**
 * Accept a connection request
 * 
 * @param {string} requestId - Request ID
 * @returns {Promise<string>} Connection ID
 */
const acceptConnectionRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'connectionRequests', requestId);
    const requestSnap = await getDoc(requestRef);
    
    if (!requestSnap.exists()) {
      throw new Error('Connection request not found');
    }
    
    const requestData = requestSnap.data();
    
    // Update request status
    await updateDoc(requestRef, {
      status: 'accepted',
      updatedAt: serverTimestamp()
    });
    
    // Create connection
    const connectionData = {
      users: [requestData.senderId, requestData.recipientId],
      createdAt: serverTimestamp(),
      requestId: requestId
    };
    
    const connectionRef = await addDoc(collection(db, 'connections'), connectionData);
    
    // Get acceptor profile for the notification
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (currentUser) {
      const acceptorProfileRef = doc(db, 'profiles', currentUser.uid);
      const acceptorProfileSnap = await getDoc(acceptorProfileRef);
      let acceptorName = 'Someone';
      let acceptorPhoto = null;
      
      if (acceptorProfileSnap.exists()) {
        const acceptorProfile = acceptorProfileSnap.data();
        acceptorName = acceptorProfile.basicInfo?.name || 'Someone';
        acceptorPhoto = acceptorProfile.photos && acceptorProfile.photos.length > 0 ? acceptorProfile.photos[0] : null;
      }
      
      // Create notification for the sender
      await createNotification({
        userId: requestData.senderId,
        type: 'connection_accepted',
        content: {
          acceptorId: currentUser.uid,
          acceptorName,
          acceptorPhoto
        }
      });
    }
    
    return connectionRef.id;
  } catch (error) {
    console.error("Error accepting connection request:", error);
    throw error;
  }
};

/**
 * Decline a connection request
 * 
 * @param {string} requestId - Request ID
 * @returns {Promise<void>}
 */
const declineConnectionRequest = async (requestId) => {
  try {
    const requestRef = doc(db, 'connectionRequests', requestId);
    
    // Update status
    await updateDoc(requestRef, {
      status: 'declined',
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error declining connection request:", error);
    throw error;
  }
};

/**
 * Get user's connections
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of connection objects with profile data
 */
const getUserConnections = async (userId) => {
  try {
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', userId)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const connections = [];
    
    for (const connectionDoc of connectionsSnapshot.docs) {
      const connectionData = connectionDoc.data();
      const otherUserId = connectionData.users.find(id => id !== userId);
      
      // Get profile data
      const profileRef = doc(db, 'profiles', otherUserId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        connections.push({
          id: connectionDoc.id,
          userId: otherUserId,
          profile: {
            id: profileSnap.id,
            ...profileSnap.data()
          },
          createdAt: connectionData.createdAt
        });
      }
    }
    
    return connections;
  } catch (error) {
    console.error("Error getting user connections:", error);
    throw error;
  }
};

/**
 * Get pending connection requests
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of pending requests with profile data
 */
const getPendingRequests = async (userId) => {
  try {
    const requestsQuery = query(
      collection(db, 'connectionRequests'),
      where('recipientId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const requestsSnapshot = await getDocs(requestsQuery);
    const requests = [];
    
    for (const requestDoc of requestsSnapshot.docs) {
      const requestData = requestDoc.data();
      
      // Get sender profile
      const profileRef = doc(db, 'profiles', requestData.senderId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        requests.push({
          id: requestDoc.id,
          senderId: requestData.senderId,
          message: requestData.message || '',
          profile: {
            id: profileSnap.id,
            ...profileSnap.data()
          },
          createdAt: requestData.createdAt
        });
      }
    }
    
    return requests;
  } catch (error) {
    console.error("Error getting pending requests:", error);
    throw error;
  }
};

/**
 * Get sent connection requests
 * 
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of sent requests with profile data
 */
const getSentRequests = async (userId) => {
  try {
    const requestsQuery = query(
      collection(db, 'connectionRequests'),
      where('senderId', '==', userId),
      where('status', '==', 'pending')
    );
    
    const requestsSnapshot = await getDocs(requestsQuery);
    const requests = [];
    
    for (const requestDoc of requestsSnapshot.docs) {
      const requestData = requestDoc.data();
      
      // Get recipient profile
      const profileRef = doc(db, 'profiles', requestData.recipientId);
      const profileSnap = await getDoc(profileRef);
      
      if (profileSnap.exists()) {
        requests.push({
          id: requestDoc.id,
          recipientId: requestData.recipientId,
          message: requestData.message || '',
          profile: {
            id: profileSnap.id,
            ...profileSnap.data()
          },
          createdAt: requestData.createdAt
        });
      }
    }
    
    return requests;
  } catch (error) {
    console.error("Error getting sent requests:", error);
    throw error;
  }
};

/**
 * Remove a connection between two users
 * Updated to handle cleanup properly
 * 
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {Promise<void>}
 */
const removeConnection = async (userId1, userId2) => {
  try {
    // Find and delete the connection
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', userId1)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const connectionDoc = connectionsSnapshot.docs.find(doc => {
      const data = doc.data();
      return data.users.includes(userId2);
    });
    
    if (connectionDoc) {
      await deleteDoc(doc(db, 'connections', connectionDoc.id));
      
      // Also update any conversations to reflect the disconnection
      const conversationId1 = `${userId1}_${userId2}`;
      const conversationId2 = `${userId2}_${userId1}`;
      
      // Try to update both possible conversation IDs
      try {
        const conv1Ref = doc(db, 'conversations', conversationId1);
        const conv1Snap = await getDoc(conv1Ref);
        if (conv1Snap.exists()) {
          await updateDoc(conv1Ref, {
            'connectionStatus.isConnected': false,
            'connectionStatus.lastChecked': serverTimestamp()
          });
        }
      } catch (error) {
        console.log('No conversation found with ID:', conversationId1);
      }
      
      try {
        const conv2Ref = doc(db, 'conversations', conversationId2);
        const conv2Snap = await getDoc(conv2Ref);
        if (conv2Snap.exists()) {
          await updateDoc(conv2Ref, {
            'connectionStatus.isConnected': false,
            'connectionStatus.lastChecked': serverTimestamp()
          });
        }
      } catch (error) {
        console.log('No conversation found with ID:', conversationId2);
      }
    }
    
  } catch (error) {
    console.error("Error removing connection:", error);
    throw error;
  }
};

/**
 * Clean up connection requests that are accepted but have no active connection
 * This should be called periodically or when removing connections
 * 
 * @param {string} userId1 - First user ID
 * @param {string} userId2 - Second user ID
 * @returns {Promise<void>}
 */
const cleanupConnectionRequests = async (userId1, userId2) => {
  try {
    // First check if there's an active connection
    const connectionsQuery = query(
      collection(db, 'connections'),
      where('users', 'array-contains', userId1),
      limit(10)
    );
    
    const connectionsSnapshot = await getDocs(connectionsQuery);
    const hasActiveConnection = connectionsSnapshot.docs.some(doc => {
      const data = doc.data();
      return data.users.includes(userId2);
    });
    
    // If no active connection, clean up accepted requests
    if (!hasActiveConnection) {
      // Find requests between these users with accepted status
      const requests1Query = query(
        collection(db, 'connectionRequests'),
        where('senderId', '==', userId1),
        where('recipientId', '==', userId2),
        where('status', '==', 'accepted')
      );
      
      const requests2Query = query(
        collection(db, 'connectionRequests'),
        where('senderId', '==', userId2),
        where('recipientId', '==', userId1),
        where('status', '==', 'accepted')
      );
      
      const [requests1Snapshot, requests2Snapshot] = await Promise.all([
        getDocs(requests1Query),
        getDocs(requests2Query)
      ]);
      
      // Delete the orphaned accepted requests
      const deletePromises = [];
      
      requests1Snapshot.docs.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      requests2Snapshot.docs.forEach(doc => {
        deletePromises.push(deleteDoc(doc.ref));
      });
      
      if (deletePromises.length > 0) {
        await Promise.all(deletePromises);
        console.log(`Cleaned up ${deletePromises.length} orphaned connection requests`);
      }
    }
    
  } catch (error) {
    console.error("Error cleaning up connection requests:", error);
  }
};

/**
 * Updated removeConnection function with cleanup
 */
const removeConnectionWithCleanup = async (userId1, userId2) => {
  try {
    // Remove the connection
    await removeConnection(userId1, userId2);
    
    // Clean up orphaned connection requests
    await cleanupConnectionRequests(userId1, userId2);
    
  } catch (error) {
    console.error("Error removing connection with cleanup:", error);
    throw error;
  }
};

/**
 * Navigate to or create a conversation with a user
 * Now allows messaging anyone, but tracks connection status
 * 
 * @param {string} userId - User ID to start a conversation with
 * @param {Function} navigate - React Router navigate function
 * @returns {Promise<void>}
 */
const startConversationWithUser = async (userId, navigate) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to start a conversation');
    }
    
    // Check connection status (but don't require it)
    const connectionStatus = await getConnectionStatus(currentUser.uid, userId);
    const isConnected = connectionStatus.status === 'connected';
    
    // Create potential conversation IDs
    const conversationId1 = `${currentUser.uid}_${userId}`;
    const conversationId2 = `${userId}_${currentUser.uid}`;
    
    // Check if conversation exists
    const conv1Ref = doc(db, 'conversations', conversationId1);
    const conv2Ref = doc(db, 'conversations', conversationId2);
    
    const [conv1Snap, conv2Snap] = await Promise.all([
      getDoc(conv1Ref),
      getDoc(conv2Ref)
    ]);
    
    let conversationId;
    
    if (conv1Snap.exists()) {
      conversationId = conversationId1;
    } else if (conv2Snap.exists()) {
      conversationId = conversationId2;
    } else {
      // Create a new conversation
      // Get profiles for both users
      const [currentUserProfileSnap, otherUserProfileSnap] = await Promise.all([
        getDoc(doc(db, 'profiles', currentUser.uid)),
        getDoc(doc(db, 'profiles', userId))
      ]);
      
      const currentUserProfile = currentUserProfileSnap.exists() ? currentUserProfileSnap.data() : {};
      const otherUserProfile = otherUserProfileSnap.exists() ? otherUserProfileSnap.data() : {};
      
      // Create conversation document with connection status metadata
      const newConversationRef = doc(db, 'conversations', conversationId1);
      await setDoc(newConversationRef, {
        participants: [currentUser.uid, userId],
        participantsInfo: {
          [currentUser.uid]: {
            name: currentUserProfile.basicInfo?.name || 'User',
            photo: currentUserProfile.photos?.[0] || null,
            username: currentUserProfile.basicInfo?.username || null
          },
          [userId]: {
            name: otherUserProfile.basicInfo?.name || 'User',
            photo: otherUserProfile.photos?.[0] || null,
            username: otherUserProfile.basicInfo?.username || null
          }
        },
        lastMessage: null,
        lastMessageTimestamp: serverTimestamp(),
        createdAt: serverTimestamp(),
        unreadCount: {
          [currentUser.uid]: 0,
          [userId]: 0
        },
        // New fields for connection tracking
        connectionStatus: {
          isConnected: isConnected,
          lastChecked: serverTimestamp()
        },
        // Track who started the conversation
        initiatedBy: currentUser.uid,
        // Security settings per user
        securitySettings: {
          [currentUser.uid]: {
            hasBlocked: false,
            hasReported: false
          },
          [userId]: {
            hasBlocked: false,
            hasReported: false,
            receivedUnconnectedMessage: false // Track if they've been notified
          }
        }
      });
      
      conversationId = conversationId1;
    }
    
    // Navigate to the conversation
    navigate(`/messages/${conversationId}`);
    
  } catch (error) {
    console.error('Error starting conversation:', error);
    throw error;
  }
};
const connectionService = {
  sendConnectionRequest,
  getConnectionStatus,
  acceptConnectionRequest,
  declineConnectionRequest,
  getUserConnections,
  getPendingRequests,
  getSentRequests,
  removeConnection,
  startConversationWithUser
};

export default connectionService;